import type { ImageLoaderProps } from 'next/image';

type Props = {
  aspectRatio?: number;
  focal?: string;
};

/**
 * A Storyblok image loader
 */
export const SBImageLoader =
  ({ aspectRatio, focal }: Props) =>
  (imageLoaderProps: ImageLoaderProps) => {
    const { src, width, quality = 75 } = imageLoaderProps;
    const height = aspectRatio ? Math.round(Number(width) * aspectRatio) : 0;

    if (!src) {
      return '';
    }

    // Don't apply filters to svg vectors
    if (src?.endsWith('.svg')) {
      return `${src}?w=${width}`;
    }

    const filters = [
      'fill(transparent)',
      focal ? `focal(${focal})` : null,
      `quality(${String(quality)})`,
      'format(webp)',
    ].filter(Boolean);

    return `${String(src)}/m/${String(width)}x${height}/filters:${filters.join(':')}`;
  };

export const SBBlurredImageLoader = async (src: string): Promise<string | ArrayBuffer | null> => {
  // We're using a2 to circumvent CORS issues.
  const urlWithCorsDomain = src.replace('https://a.storyblok.com/', 'https://a2.storyblok.com/');
  const url = `${String(urlWithCorsDomain)}/m/400x0/filters:blur(20):quality(10)`;

  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load blurred image:', error);
    return null;
  }
};

/**
 * it will use the focus property of Storyblok to calculate the focal point
 * example URL: https://a.storyblok.com/f/148915/1024x683/1cd8b7d0f7/klimrijk_brabant_groep_e-chopper_rijden_bossen.jpg
 * example focus: 930x557:931x558
 */
export const SBPositionBasedOnFocalPoint = (src: string, focus?: string) => {
  if (!src || !focus) {
    return null;
  }
  const [imageWidth, imageHeight] = src?.split('/')?.[5]?.split('x') ?? [0, 0];
  const [focusWidth, focusHeight] = focus?.split?.(':')?.[0]?.split('x') ?? [0, 0];

  const percentageWidth = Math.round((Number(focusWidth) / Number(imageWidth)) * 100);
  const percentageHeight = Math.round((Number(focusHeight) / Number(imageHeight)) * 100);
  return `${percentageWidth}% ${percentageHeight}%`;
};
